import type { Recht } from "../../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Die Ordnung der Einstellungen — seit dem 04.10.2026 an EINER Stelle.

   Luca, 04.10.2026: *„Ich denke, es ist langsam an der Zeit, eine
   Korrektur oder Anpassung des Settings-Menüs vorzunehmen, also eine
   Neustrukturierung, weil wir jetzt ja sehr, sehr viele Punkte da
   mittlerweile haben."*

   Bis dahin standen fünf Gruppen nach Technik da — Betrieb, Regeln,
   Belege, Lexware Office, System —, „Regeln" mit acht Blättern, „Belege"
   mit einem, und zwei Punkte hießen fast gleich („Benutzer und Rechte",
   „Rollen und Rechte"). Wer die Bindefrist suchte, fand sie unter
   „Regeln › Angebot", das Briefpapier unter „Firma und Logo", die
   Stunden eines Arbeitstags unter „Einsatz".

   **Jetzt nach den Leuten, die sie suchen** — Verwaltung, Disposition,
   Buchhaltung: acht Gruppen, jede Seite genau einmal. Die Begründung je
   Seite und die Tabelle alt → neu stehen in
   `docs/57-einstellungen-struktur.md`.

   **Die Seite steht in der Adresse** (`/einstellungen/<seite>`). Bis
   zum 04.10.2026 stand sie im Zustand („eine Einstellung ist nichts,
   worauf man einen Link schickt") — aber das Handbuch, die Meldungen und
   „Was hier hakt" wollen genau dorthin führen, und der Zurück-Knopf des
   Browsers soll vom Blatt auf die Liste gehen.

   Die Datei ist rein — kein React, kein Router —, damit sich Ordnung,
   Suche, Rechte und Umleitungen ohne Browser prüfen lassen
   (`test/einstellungsseiten.test.ts`).
   ══════════════════════════════════════════════════════════════════ */

/** Jede Seite der Einstellungen — der Name ist zugleich ihr Stück der Adresse. */
export type SeiteId =
  // Betrieb & Firma
  | "firma" | "erscheinungsbild" | "dokumente"
  // Mitarbeiter & Rechte
  | "mitarbeiter" | "rollen" | "anmeldeprotokoll"
  // Vermieten & Preise
  | "angebote" | "miettage" | "pflichtfelder"
  // Planung & Draußen
  | "vorbereitung" | "transportpuffer" | "einsatz" | "checklisten" | "pflichtfotos"
  | "zaehler"
  // Papiere & Texte
  | "briefpapier"
  // Meldungen & Mail
  | "ereignisse" | "empfaenger" | "empfaengergruppen" | "morgenmail" | "meldetest"
  // Lexware Office
  | "lexware" | "wer-hat-was"
  // System & Läufe
  | "laeufe" | "archiv" | "nummernkreise" | "import-export" | "fassung" | "handbuch";

export type GruppenId =
  | "betrieb" | "mitarbeiter" | "vermieten" | "planung"
  | "papiere" | "meldungen" | "lexware" | "system";

/**
 * Eine Seite der Einstellungen.
 *
 * `worte` ist ein i18n-Schlüssel auf eine Liste von Stichworten, durch
 * Komma getrennt — das, wonach jemand sucht, der den Namen der Seite nicht
 * kennt („Bindefrist", „Adresse", „Cron"). Sichtbar ist sie nie; sie ist
 * trotzdem Sprache und gehört deshalb nicht in den Code.
 *
 * `recht`: was das Konto braucht, um die Seite zu sehen. `null` heißt jeder,
 * der in die Einstellungen kommt.
 */
export type Seite = {
  art: "seite";
  id: SeiteId;
  punkt: string;
  titel: string;
  satz: string;
  worte: string;
  recht: Recht | null;
};

/**
 * Ein Verweis in die Stammdaten — **kein** eigenes Blatt.
 *
 * Was es gibt und was es kostet, bleibt in den Stammdaten (Teil 10 des
 * Handbuchs). **Seit dem 08.10.2026 stehen keine Verweise mehr im Menü**
 * (Luca: „Die Stammdaten haben da nichts zu suchen. Das macht das Menü nur
 * unnötig groß.“). Die Bauart bleibt, falls ein Verweis wiederkommt.
 */
export type Verweis = {
  art: "verweis";
  id: string;
  punkt: string;
  satz: string;
  worte: string;
  recht: Recht;
  weg: "/weiteres" | "/preise";
  blatt?: string;
};

export type Eintrag = Seite | Verweis;

export type Gruppe = { id: GruppenId; titel: string; eintraege: Eintrag[] };

const E = "einstellungen.struktur";

function seite(id: SeiteId, punkt: string, titel: string, satz: string, recht: Recht | null = "verwalten"): Seite {
  return { art: "seite", id, punkt, titel, satz, worte: `${E}.worte.${id}`, recht };
}


/**
 * Die Ordnung. Reihenfolge der Gruppen: erst was der Betrieb IST und wer
 * darin arbeitet, dann die drei Arbeitsfelder in der Reihenfolge eines
 * Auftrags (verkaufen → planen → Papier), dann was im Hintergrund läuft.
 */
export const GRUPPEN: Gruppe[] = [
  {
    id: "betrieb",
    titel: `${E}.gruppe.betrieb`,
    eintraege: [
      seite("firma", `${E}.punkt.firma`, `${E}.punkt.firma`, `${E}.satz.firma`),
      /* Akzentfarbe und App-Logo (Signal, Paket 6) — gleich nach der Firma:
         wer das Logo sucht, sucht es beim Betrieb. Nur `verwalten`.
         **Seit dem 08.10.2026 mit den Kalenderfarben** als zweitem Reiter
         (Luca: „Dann sparen wir uns einen Menüpunkt."). Wie etwas aussieht,
         steht an einer Stelle — die Farbe im Brett ist auch eine Farbe. */
      seite("erscheinungsbild", `${E}.punkt.erscheinungsbild`, `${E}.punkt.erscheinungsbild`, `${E}.satz.erscheinungsbild`),
      seite("dokumente", "einstellungen.punktDokumente", "einstellungen.dokumenteTitel", "einstellungen.dokumenteSatz"),
    ],
  },
  {
    id: "mitarbeiter",
    titel: `${E}.gruppe.mitarbeiter`,
    eintraege: [
      seite("mitarbeiter", `${E}.punkt.mitarbeiter`, `${E}.punkt.mitarbeiter`, "einstellungen.benutzerSatz"),
      seite("rollen", `${E}.punkt.rollen`, `${E}.punkt.rollen`, "einstellungen.rollenSatz"),
      seite("anmeldeprotokoll", `${E}.punkt.anmeldeprotokoll`, `${E}.punkt.anmeldeprotokoll`, "einstellungen.protokollSatz"),
    ],
  },
  {
    id: "vermieten",
    titel: `${E}.gruppe.vermieten`,
    eintraege: [
      seite("angebote", `${E}.punkt.angebote`, `${E}.punkt.angebote`, "einstellungen.angebotSatz"),
      seite("miettage", `${E}.punkt.miettage`, `${E}.punkt.miettage`, `${E}.satz.miettage`),
      seite("pflichtfelder", `${E}.punkt.pflichtfelder`, "einstellungen.pflichtfelderTitel", "einstellungen.pflichtfelderSatz"),
    ],
  },
  {
    id: "planung",
    titel: `${E}.gruppe.planung`,
    eintraege: [
      seite("vorbereitung", "einstellungen.punktVorbereitung", "einstellungen.vorbereitungTitel", "einstellungen.vorbereitungSatz"),
      seite("transportpuffer", `${E}.punkt.transportpuffer`, `${E}.punkt.transportpuffer`, `${E}.satz.transportpuffer`),
      /* **Hier kommt der Schalter „Qualifikation: warnen | sperren" hin**
         (andere Sitzung, 04.10.2026) — als Bogen auf dieser Seite
         (`Betrieb.tsx`, `BlattEinsatz`), nicht als eigene Seite. Die
         Stichworte „Qualifikation" und „Bediener" stehen schon in
         `einstellungen.struktur.worte.einsatz`, damit die Suche ihn findet. */
      seite("einsatz", `${E}.punkt.einsatz`, `${E}.punkt.einsatz`, `${E}.satz.einsatz`),
      seite("checklisten", "einstellungen.punktChecklisten", "einstellungen.checklistenTitel", "einstellungen.checklistenSatz"),
      seite("pflichtfotos", "einstellungen.punktPflichtfotos", "einstellungen.pflichtfotosTitel", "einstellungen.pflichtfotosSatz"),
      seite("zaehler", "einstellungen.punktZaehler", "einstellungen.zaehlerTitel", "einstellungen.zaehlerSatz"),
    ],
  },
  {
    id: "papiere",
    titel: `${E}.gruppe.papiere`,
    eintraege: [
      seite("briefpapier", `${E}.punkt.briefpapier`, `${E}.punkt.briefpapier`, `${E}.satz.briefpapier`),
    ],
  },
  {
    id: "meldungen",
    titel: `${E}.gruppe.meldungen`,
    eintraege: [
      seite("ereignisse", `${E}.punkt.ereignisse`, `${E}.punkt.ereignisse`, `${E}.satz.ereignisse`),
      seite("empfaenger", `${E}.punkt.empfaenger`, `${E}.punkt.empfaenger`, `${E}.satz.empfaenger`),
      seite("empfaengergruppen", `${E}.punkt.empfaengergruppen`, `${E}.punkt.empfaengergruppen`, `${E}.satz.empfaengergruppen`),
      seite("morgenmail", `${E}.punkt.morgenmail`, `${E}.punkt.morgenmail`, `${E}.satz.morgenmail`),
      seite("meldetest", `${E}.punkt.meldetest`, `${E}.punkt.meldetest`, `${E}.satz.meldetest`),
    ],
  },
  {
    id: "lexware",
    titel: `${E}.gruppe.lexware`,
    eintraege: [
      seite("lexware", "einstellungen.punktLexware", "einstellungen.lexwareTitel", "einstellungen.lexwareSatz"),
      seite("wer-hat-was", `${E}.punkt.werHatWas`, "einstellungen.infoTitel", "einstellungen.infoSatz"),
    ],
  },
  {
    id: "system",
    titel: `${E}.gruppe.system`,
    eintraege: [
      seite("laeufe", `${E}.punkt.laeufe`, `${E}.punkt.laeufe`, "einstellungen.jobsSatz"),
      /* **Archiv (GoBD)** (05.10.2026, docs/58): Anbindung der Ablage mit
         Löschsperre, Fristen, Bestand, „darf weg ab …", Export. Bei den
         Läufen, weil zwei Läufe es tragen (`archiv`, `archiv_pruefung`). */
      /* Recht `pruefen` (0274): Die Verwaltung bündelt es, der Prüfer hat es
         — er sieht in den Einstellungen nur diese eine Seite (B7). */
      seite("archiv", `${E}.punkt.archiv`, `${E}.punkt.archiv`, `${E}.satz.archiv`, "pruefen"),
      /* **Nummernkreise** (08.10.2026, aus „Papiere & Texte"; nach dem
         Archiv, das an den Läufen hängt): Ein Zähler
         ist kein Papier — er läuft im Hintergrund wie die Läufe, und wer ihn
         anfasst, ist die Verwaltung, nicht wer ein Briefpapier sucht. */
      seite("nummernkreise", "einstellungen.punktNummern", "einstellungen.nummernTitel", "einstellungen.nummernSatz"),
      /* **Import / Export** (05.10.2026, docs/63): Stammdaten als Excel oder
         CSV herausgeben und einspielen — für den Umstieg auf Machine Office. */
      seite("import-export", `${E}.punkt.importExport`, `${E}.punkt.importExport`, `${E}.satz.importExport`),
      seite("fassung", "einstellungen.punktFassung", "einstellungen.fassungTitel", "einstellungen.fassungSatz"),
      /* Das Handbuch hängt an keinem Recht (`router.tsx`, `/handbuch`). */
      seite("handbuch", "einstellungen.punktHandbuch", "einstellungen.handbuchTitel", "einstellungen.handbuchSatz", null),
    ],
  },
];

/** Die Seite, die am Schreibtisch offen ist, solange die Adresse keine nennt. */
export const ERSTE_SEITE: SeiteId = "firma";

/**
 * **Die alten Namen der Blätter → die neuen Seiten** (04.10.2026).
 *
 * Adressen gab es für die Blätter bis heute nicht — sie standen im Zustand.
 * Die alten Namen leben aber im Handbuch, in Notizen und in Köpfen
 * („Regeln › Kalender"), und wer `/einstellungen/jobs` tippt, soll bei den
 * Läufen ankommen und nicht auf einer leeren Seite. Ein Name, der sich
 * nicht geändert hat, steht hier nicht.
 *
 * Wohin ein geteiltes Blatt führt:
 *   - „Firma und Logo" (`betrieb`) → Firma; das Briefpapier ist eine eigene Seite.
 *   - „Kalenderregeln" (`kalender`) → Transportpuffer; die Miettage stehen
 *     jetzt mit dem Arbeitstag zusammen unter „Miettage und Arbeitstag". Der
 *     Puffer ist, wohin die Meldung „Das ist ein Transportpuffer" führt.
 *   - „Benachrichtigungen" → Ereignisse, die erste ihrer fünf Seiten.
 */
export const ALTE_SEITEN: Readonly<Record<string, SeiteId>> = {
  betrieb: "firma",
  benutzer: "mitarbeiter",
  farben: "erscheinungsbild",
  // Eigene Seite bis zum 08.10.2026, jetzt ein Reiter im Erscheinungsbild.
  kalenderfarben: "erscheinungsbild",
  angebot: "angebote",
  kalender: "transportpuffer",
  nummern: "nummernkreise",
  info: "wer-hat-was",
  jobs: "laeufe",
  benachrichtigungen: "ereignisse",
  protokoll: "anmeldeprotokoll",
};

const ALLE_SEITEN: Seite[] = GRUPPEN.flatMap((g) => g.eintraege.filter((e): e is Seite => e.art === "seite"));

export function istSeite(roh: string): roh is SeiteId {
  return ALLE_SEITEN.some((s) => s.id === roh);
}

/**
 * Was eine Adresse meint: eine Seite, eine alte Seite (→ Umleitung) oder
 * nichts (→ die Übersicht).
 */
export function seiteAusAdresse(roh: string | undefined): { seite: SeiteId | null; umleiten: boolean } {
  if (!roh) return { seite: null, umleiten: false };
  if (istSeite(roh)) return { seite: roh, umleiten: false };
  const neu = ALTE_SEITEN[roh];
  if (neu) return { seite: neu, umleiten: true };
  return { seite: null, umleiten: true };
}

export function seiteZu(id: SeiteId): Seite {
  const s = ALLE_SEITEN.find((x) => x.id === id);
  if (!s) throw new Error(`Unbekannte Einstellungsseite: ${id}`);
  return s;
}

export function gruppeZu(id: SeiteId): Gruppe | undefined {
  return GRUPPEN.find((g) => g.eintraege.some((e) => e.art === "seite" && e.id === id));
}

/** Nur, was dieses Konto sehen darf; eine Gruppe ohne Eintrag fällt weg. */
export function sichtbareGruppen(rechte: readonly Recht[]): Gruppe[] {
  return GRUPPEN
    .map((g) => ({ ...g, eintraege: g.eintraege.filter((e) => e.recht === null || rechte.includes(e.recht)) }))
    .filter((g) => g.eintraege.length > 0);
}

/**
 * **Wer die Einstellungen überhaupt öffnet** (Befund P5-02, 05.10.2026): wer
 * mindestens eine Seite sieht, die an einem Recht hängt. Bis dahin fragte die
 * Maske pauschal `verwalten` — der Prüfer (Recht `pruefen`, 0274) stand vor
 * „Dafür fehlt dir das Recht", obwohl diese Tabelle ihm das Archiv zeigt und
 * der Weg `/einstellungen` ihn hereinlässt (`schale/rechte.ts`). Das Handbuch
 * (`recht: null`) zählt nicht: Es steht jedem auch unter `/handbuch` offen,
 * und ein Einstellungsschirm mit nur dem Handbuch wäre keiner.
 */
export function einstellungenOffen(rechte: readonly Recht[]): boolean {
  return sichtbareGruppen(rechte).some((g) => g.eintraege.some((e) => e.art === "seite" && e.recht !== null));
}

/** Klein, ohne Akzente und Umlaute — „Zähler" findet man auch mit „zahler" und „zaehler". */
export function suchform(s: string): string {
  return s
    // Ein Satz darf `<b>` tragen; gesucht wird im Text, nicht im Auszeichnen.
    .replace(/<[^>]+>/g, " ")
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/**
 * **Die Suche über allen Einstellungen.** Jedes Wort der Eingabe muss
 * irgendwo stehen — im Namen der Seite, in ihrem Satz, in ihren
 * Stichworten oder im Namen ihrer Gruppe. Passt der Gruppenname, bleibt die
 * ganze Gruppe stehen: Wer „Lexware" tippt, will beide Seiten sehen.
 *
 * `t` übersetzt einen Schlüssel; die Suche läuft über das, was dasteht,
 * nicht über die Schlüssel.
 */
export function sucheIn(gruppen: Gruppe[], eingabe: string, t: (schluessel: string) => string): Gruppe[] {
  const woerter = suchform(eingabe).split(/[\s,]+/).filter(Boolean);
  if (woerter.length === 0) return gruppen;
  return gruppen
    .map((g) => {
      return {
        ...g,
        eintraege: g.eintraege.filter((e) => {
          const titel = e.art === "seite" ? t(e.titel) : "";
          const roh = [t(e.punkt), titel, t(e.satz), t(e.worte), t(g.titel)].join(" ");
          // Beide Schreibweisen: „zaehler" und „zahler" finden „Zähler".
          const text = `${suchform(roh)} ${suchform(roh.replace(/[äöü]/gi, (u) => u.normalize("NFD")[0] ?? u))}`;
          return woerter.every((w) => text.includes(w));
        }),
      };
    })
    .filter((g) => g.eintraege.length > 0);
}
